import React, {FC, memo, useEffect, useMemo, useState} from 'react'

import ViewContextTableIcon from '@pixel/assets/icons/ViewContextTable'

import ListBase from '@pixel/core/ListBase'

import { styled } from '@pixel/core/styles'
import useFieldListsListQuery from '../hooks/useFieldListsListQuery'
import { ListBaseSkeletonLoading } from '../../components/ListBaseDefaultLoading'
import FieldListsDataTable, { FieldListsDataTableColumnNames } from './FieldListsDataTable'
import { useMultipleLocalizedText } from '../../contexts/Localization/localizationHooks'
import { FieldListSearchResult } from '../types/FieldList'
import {useQueryClient} from "@tanstack/react-query";

const TABLE_PER_PAGE_OPTIONS = [
    { value: 10 },
    { value: 25 },
    { value: 50 },
]

const FieldListsListBaseContainer = styled('section')(({ theme }: { theme: any }) => ({
    backgroundColor: '#F5F8FA',
    padding: theme.spacing(6),
    paddingTop: theme.spacing(2),
}))

export type FieldListSelectedCallback = (fieldList: FieldListSearchResult) => void

export interface FieldListsListBaseNodeContext {
    onFieldListSelected: FieldListSelectedCallback
    viewFieldListSelected: FieldListSelectedCallback
    changeFieldListStatus: FieldListSelectedCallback
}

interface FieldListsListBaseProps {
    onFieldListSelected: FieldListSelectedCallback
    viewFieldListSelected: FieldListSelectedCallback
    changeFieldListStatus: FieldListSelectedCallback
    fetchData: () => void
    refetch: boolean
}
export const FieldListsListBase: FC<FieldListsListBaseProps> = ({
    onFieldListSelected,
    viewFieldListSelected,
    changeFieldListStatus,
    fetchData,
    refetch,
}) => {
    const {
        FIELD_LIST_ROWS_PER_PAGE,
        COMMON_VIEW_DATATABLE,
    } = useMultipleLocalizedText()

    const [page, setPage] = useState(0)
    const [perPage, setPerPage] = useState(TABLE_PER_PAGE_OPTIONS[0].value)

    const queryClient = useQueryClient()

    const {
        data,
        status,
    } = useFieldListsListQuery({
        page,
        perPage
    })


    useEffect(() => {
        if(refetch) {
            queryClient.invalidateQueries({
                queryKey: ['fieldLists']
            })
            fetchData();
        }
    },[refetch, queryClient, fetchData])

    const nodeContext = useMemo(() => ({ onFieldListSelected, viewFieldListSelected, changeFieldListStatus }), [onFieldListSelected, viewFieldListSelected, changeFieldListStatus])

    return (
        <FieldListsListBaseContainer>
            <ListBase
                showSearch={false}
                getRowID={(node) => node.fieldListId}
                pagingContext={{
                    page: page + 1,
                    perPage: perPage,
                    first: page * perPage + 1,
                    last: Math.min(page * perPage + perPage, data?.totalCount ?? 0),
                    hideFirstLastActions: true,
                }}
                rowsPerPageLabel={FIELD_LIST_ROWS_PER_PAGE}
                resultsPerPage={perPage}
                perPageOptions={TABLE_PER_PAGE_OPTIONS}
                onPageChanged={(e: any, nextPage: number) => {
                    setPage(nextPage - 1);
                }}
                onPaginationChange={(e: any) => {
                    setPage(0)
                    setPerPage(e.target.value)
                }}
                results={data || { nodes: [], totalCount: 0 }}
                resultsLoading={status === 'loading'}
                nodeContext={nodeContext}
                viewContexts={[
                    {
                        ariaLabel: COMMON_VIEW_DATATABLE,
                        icon: <ViewContextTableIcon />,
                        renderResults: (viewContextProps: any) => (
                            <FieldListsDataTable
                                viewContextProps={viewContextProps}
                                dataStatus={status}
                                totalCount={data?.totalCount ?? 0}
                                onRefetch={() => {

                                }}
                                onFieldListSelected={onFieldListSelected}
                                viewFieldListSelected={viewFieldListSelected}
                                changeFieldListStatus={changeFieldListStatus}
                            />
                        ),
                        renderLoading: () => (
                            <ListBaseSkeletonLoading
                                columns={FieldListsDataTableColumnNames}
                                items={5}
                            />
                        )
                    }
                ]}
                labelSearch=""
                labelSearchAria=""
                labelFilters=""
                labelClose=""
                labelApplyFilters=""
                labelClearAllFilters=""
            />
        </FieldListsListBaseContainer>
    )
}

export default memo(FieldListsListBase)
